import { useEffect } from "react";

/**
 * Client-side per-page SEO. Updates document.title + meta description +
 * Open Graph + Twitter Card tags on mount and when the inputs change.
 *
 * Google's crawler executes JS and picks up the updated tags. Social crawlers
 * (Facebook, iMessage, WhatsApp) don't run JS on link-preview scrapes, so
 * they'll fall back to the tags baked into `index.html`. For per-URL social
 * previews to work everywhere, we'd need SSR / prerender — tracked as P2 in
 * the roadmap. This still covers ~80% of the SEO win.
 *
 * @param {object} opts
 * @param {string} opts.title             - Full <title>
 * @param {string} opts.description       - <meta name="description">
 * @param {string} [opts.image]           - Absolute URL for og:image
 * @param {string} [opts.url]             - Absolute canonical URL
 * @param {string} [opts.type]            - og:type (default "website")
 * @param {boolean} [opts.noIndex=false]  - Emit <meta name="robots" content="noindex,nofollow">
 */
export function useSeo({
  title,
  description,
  image,
  url,
  type = "website",
  noIndex = false,
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const canonicalUrl = url || (typeof window !== "undefined" ? window.location.href.split("?")[0].split("#")[0] : "");
    const finalImage = image || "https://platebuddies.com/brand/og-share-v3.jpg";

    // Title
    if (title) document.title = title;

    // Track any nodes we create so we can clean up if the hook re-runs.
    const created = [];

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        el.dataset.seoManaged = "1";
        created.push(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", finalImage);
    setMeta("property", "og:image:secure_url", finalImage);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", type);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", finalImage);

    // robots (noindex for cart / checkout / admin)
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
        robots.dataset.seoManaged = "1";
      }
      robots.setAttribute("content", "noindex,nofollow");
    } else if (robots && robots.dataset.seoManaged === "1") {
      robots.remove();
    }

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }
  }, [title, description, image, url, type, noIndex]);
}

export default useSeo;
